import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, Plus, Search, Send } from 'lucide-react';
import customerSmsService from '../../services/CustomerSmsService';

const errorText = (error) => error?.response?.data?.message || 'Unable to load messages. Please try again.';
const timestamp = (value) => new Date(value).toLocaleString();

export default function CustomerSmsInbox() {
  const [conversations, setConversations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [draft, setDraft] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const selectionVersion = useRef(0);
  const pendingSend = useRef(null);
  const sendLock = useRef(false);
  const bottom = useRef(null);

  const loadConversations = useCallback(async (initial = false) => {
    const rows = await customerSmsService.conversations();
    setConversations(rows);
    if (initial) {
      setSelected(rows[0] || null);
      setPhoneNumber(rows[0]?.phoneNumber || '');
    }
  }, []);

  useEffect(() => {
    loadConversations(true)
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, [loadConversations]);

  const loadHistory = useCallback(async (customerId, before, version) => {
    setHistoryLoading(true);
    try {
      const page = await customerSmsService.history(customerId, before);
      if (version !== selectionVersion.current) return;
      setMessages((old) => (before ? [...page.messages, ...old] : page.messages));
      setCursor(page.nextCursor);
    } catch (e) {
      if (version === selectionVersion.current) setError(errorText(e));
    } finally {
      if (version === selectionVersion.current) setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    const version = ++selectionVersion.current;
    setMessages([]);
    setCursor(null);
    if (selected?.customerId) loadHistory(selected.customerId, null, version);
  }, [selected?.customerId, loadHistory]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  async function newChat() {
    setChoosing(true);
    setSearch('');
    setError('');
    setLoading(true);
    try {
      setCustomers(await customerSmsService.customers());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }

  function selectCustomer(customer) {
    if (sending) return;
    setSelected(customer);
    setPhoneNumber(customer.phoneNumber || customer.rawPhoneNumber || '');
    setDraft('');
    pendingSend.current = null;
    setError('');
    setChoosing(false);
    setSearch('');
  }

  async function send(event) {
    event.preventDefault();
    if (sendLock.current || !selected || !draft.trim() || !phoneNumber.trim()) return;
    sendLock.current = true;
    setSending(true);
    setError('');
    const body = draft.trim();
    // Reuse the same ID after a network error, even if the original request completed.
    if (
      !pendingSend.current ||
      pendingSend.current.customerId !== selected.customerId ||
      pendingSend.current.body !== body ||
      pendingSend.current.phoneNumber !== phoneNumber.trim()
    ) {
      pendingSend.current = {
        customerId: selected.customerId,
        phoneNumber: phoneNumber.trim(),
        body,
        requestId: `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
      };
    }
    try {
      const message = await customerSmsService.send(pendingSend.current);
      setMessages((old) => [...old.filter((m) => m._id !== message._id), message]);
      setDraft('');
      setPhoneNumber(message.phoneNumber);
      pendingSend.current = null;
      if (message.errorMessage) setError(message.errorMessage);
      await loadConversations();
    } catch (e) {
      setError(errorText(e));
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  }

  const query = search.toLowerCase().trim();
  const rows = (choosing ? customers : conversations).filter((c) =>
    `${c.displayName} ${c.phoneNumber || ''} ${c.rawPhoneNumber || ''}`.toLowerCase().includes(query)
  );

  return (
    <section className="tw-p-6 tw-text-[#243343] max-[700px]:tw-p-3" aria-label="Customer SMS inbox">
      <h2 className="tw-flex tw-items-center tw-gap-2.5 tw-text-2xl tw-mb-5">
        <MessageCircle size={24} /> Customer messages
      </h2>
      {error && (
        <div role="alert" className="tw-bg-[#fff0ee] tw-text-[#9b3024] tw-p-3 tw-mb-3 tw-rounded-md">
          {error}
        </div>
      )}

      <div className="tw-grid tw-grid-cols-[40%_60%] tw-h-[72vh] tw-min-h-[480px] tw-border tw-border-solid tw-border-[#dce3e9] tw-rounded-xl tw-overflow-hidden tw-bg-white max-[700px]:tw-grid-cols-1 max-[700px]:tw-h-auto">
        <div className="tw-flex tw-flex-col tw-min-w-0 tw-min-h-0 tw-border-0 tw-border-r tw-border-solid tw-border-[#dce3e9] max-[700px]:tw-h-[330px] max-[700px]:tw-border-r-0 max-[700px]:tw-border-b">
          <div className="tw-p-[18px] tw-border-0 tw-border-b tw-border-solid tw-border-[#e5eaf0]">
            <label className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-mb-2" htmlFor="customer-sms-search">
              <Search size={18} /> {choosing ? 'QuickBooks customers' : 'Message history'}
            </label>
            <input
              className="tw-w-full tw-p-2.5 tw-border tw-border-solid tw-border-[#cad4df] tw-rounded-md"
              id="customer-sms-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or phone number"
            />
          </div>
          <div className="tw-flex-1 tw-overflow-y-auto" aria-busy={loading}>
            {loading ? (
              <p className="tw-p-6 tw-text-center tw-text-[#64748b]">Loading…</p>
            ) : (
              rows.map((customer) => (
                <button
                  type="button"
                  key={customer.customerId}
                  disabled={sending}
                  className={`tw-flex tw-flex-col tw-gap-1 tw-w-full tw-text-left tw-border-0 tw-border-b tw-border-solid tw-border-[#edf0f4] tw-py-4 tw-px-[18px] tw-text-inherit hover:tw-bg-[#edf5fb] tw-break-words disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px] ${
                    selected?.customerId === customer.customerId ? 'tw-bg-[#edf5fb]' : 'tw-bg-white'
                  }`}
                  onClick={() => selectCustomer(customer)}
                >
                  <strong>{customer.displayName}</strong>
                  <span>{customer.phoneNumber || customer.rawPhoneNumber || 'No phone number'}</span>
                  {choosing && !customer.phoneNumber && (
                    <small className="tw-text-[#526579] tw-text-xs">Select to enter a phone number</small>
                  )}
                  {!choosing && (
                    <>
                      <span className="tw-max-w-full tw-truncate">{customer.body}</span>
                      <small className="tw-text-[#526579] tw-text-xs">{timestamp(customer.createdAt)}</small>
                    </>
                  )}
                </button>
              ))
            )}
            {!loading && !rows.length && (
              <p className="tw-p-6 tw-text-center tw-text-[#64748b]">
                {choosing ? 'No matching customers.' : 'No conversations yet. Start a new chat.'}
              </p>
            )}
          </div>
          <div className="tw-p-3.5 tw-flex tw-flex-wrap tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-[#e5eaf0]">
            {choosing && (
              <button
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px]"
                type="button"
                onClick={() => {
                  setChoosing(false);
                  setSearch('');
                }}
              >
                Back to history
              </button>
            )}
            <button
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px]"
              type="button"
              disabled={sending || loading}
              onClick={newChat}
            >
              <Plus size={18} /> {choosing ? 'Refresh customers' : 'New chat'}
            </button>
            {!choosing && (
              <button
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px]"
                type="button"
                disabled={loading || sending}
                onClick={() => {
                  setError('');
                  loadConversations(!selected).catch((e) => setError(errorText(e)));
                  if (selected) loadHistory(selected.customerId, null, selectionVersion.current);
                }}
              >
                Refresh history
              </button>
            )}
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-min-w-0 tw-min-h-0 max-[700px]:tw-h-[550px]">
          {selected ? (
            <>
              <header className="tw-flex tw-flex-col tw-gap-1 tw-p-[18px] tw-border-0 tw-border-b tw-border-solid tw-border-[#e5eaf0]">
                <strong>{selected.displayName}</strong>
                <label
                  className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-mb-2"
                  htmlFor="customer-sms-phone"
                >
                  Recipient phone number
                </label>
                <input
                  className="tw-w-full tw-p-2.5 tw-border tw-border-solid tw-border-[#cad4df] tw-rounded-md"
                  id="customer-sms-phone"
                  type="tel"
                  value={phoneNumber}
                  disabled={sending}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter a phone number, e.g. 0412 345 678"
                  aria-describedby="customer-sms-phone-help"
                />
                <small className="tw-text-[#526579] tw-text-xs" id="customer-sms-phone-help">
                  Used for this SMS. QuickBooks contact details are unchanged.
                </small>
              </header>

              <div className="tw-flex-1 tw-overflow-y-auto tw-p-5 tw-bg-[#f6f8fb]" aria-busy={historyLoading}>
                {cursor && (
                  <button
                    className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px]"
                    type="button"
                    disabled={historyLoading || sending}
                    onClick={() => loadHistory(selected.customerId, cursor, selectionVersion.current)}
                  >
                    Load older messages
                  </button>
                )}
                {historyLoading && <p className="tw-p-6 tw-text-center tw-text-[#64748b]">Loading history…</p>}
                {!historyLoading && !messages.length && (
                  <p className="tw-p-6 tw-text-center tw-text-[#64748b]">
                    Write your first message to {selected.displayName}.
                  </p>
                )}
                {messages.map((message) => (
                  <article
                    className="tw-w-fit tw-max-w-[88%] tw-m-0 tw-mb-4 tw-ml-auto tw-bg-[#e0effa] tw-py-3 tw-px-4 tw-rounded-xl tw-rounded-br-[2px] tw-break-words"
                    key={message._id}
                  >
                    <p className="tw-whitespace-pre-wrap tw-m-0 tw-mb-2">{message.body}</p>
                    <small className="tw-block tw-text-[#526579] tw-text-xs">To: {message.phoneNumber}</small>
                    <small className="tw-block tw-text-[#526579] tw-text-xs">
                      {timestamp(message.createdAt)} ·{' '}
                      {message.status === 'queued' ? 'Accepted by provider' : message.status} · {message.senderId}
                    </small>
                    {message.errorMessage && (
                      <small className="tw-block tw-text-[#526579] tw-text-xs" role="status">
                        {message.errorMessage}
                      </small>
                    )}
                  </article>
                ))}
                <div ref={bottom} />
              </div>

              <form className="tw-p-[18px] tw-border-0 tw-border-t tw-border-solid tw-border-[#e5eaf0]" onSubmit={send}>
                <label
                  className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-mb-2"
                  htmlFor="customer-sms-body"
                >
                  Message from Boxshall
                </label>
                <textarea
                  className="tw-w-full tw-p-2.5 tw-border tw-border-solid tw-border-[#cad4df] tw-rounded-md tw-resize-y tw-max-h-[180px]"
                  id="customer-sms-body"
                  rows={3}
                  maxLength={1600}
                  value={draft}
                  disabled={sending}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Write a text message…"
                />
                <div className="tw-flex tw-justify-between tw-items-center tw-gap-3 tw-mt-2">
                  <small className="tw-text-[#526579] tw-text-xs">
                    {draft.length}/1600 · SMS from Boxshall cannot receive replies.
                  </small>
                  <button
                    className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px]"
                    type="submit"
                    disabled={sending || historyLoading || !draft.trim() || !phoneNumber.trim()}
                  >
                    <Send size={17} />
                    {sending ? 'Sending…' : 'Send SMS'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <p className="tw-p-6 tw-text-center tw-text-[#64748b]">Select a conversation or start a new chat.</p>
          )}
        </div>
      </div>
    </section>
  );
}
